import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CardImage } from "@/components/CardImage";
import { FaqSection } from "@/components/PageFaq";
import { ClusterLinks } from "@/components/ClusterLinks";
import { DefaultNotFound, DefaultRouteError } from "@/components/RouteFallbacks";
import { useSession } from "@/lib/user-data";
import { cardLinkProps } from "@/lib/card-link";
import { OFFICIAL_CARD_BY_CODE, PLAYABLE_CARDS, officialCardImage, searchOfficialCards, resolveOfficialCardCode } from "@/lib/tcg-cards";
import {
  addWantEntry,
  getMyWantList,
  listWantBoard,
  removeWantEntry,
} from "@/lib/want-list.functions";
import {
  groupWantBoard,
  isMatched,
  WANT_LIMIT,
  type WantBoardCard,
  type WantEntry,
  type WantKind,
} from "@/lib/want-list-shared";

const FAQ = [
  {
    q: "What is a Palworld TCG want list?",
    a: "A want list is a public post of the Palworld Official Card Game cards you are hunting, next to the spare cards you can trade away. On the Pal Stars want-list board every listing is visible to anyone — no account needed to read it — so collectors can see at a glance who needs which card before a single message is sent.",
  },
  {
    q: "Do I need an account to use the Palworld want-list board?",
    a: "No account is needed to browse the board or to see which cards are matched. You only need a free Pal Stars account to post your own wants and spares and to send the actual trade offer.",
  },
  {
    q: "How does a match work on the want-list board?",
    a: "A card is marked matched when at least one member wants it and another member has a spare of the same printing. Open the card, see who holds the spare, then send the swap from the Pal Stars card trading page.",
  },
  {
    q: "How many cards can I put on my want list?",
    a: `Each account can keep up to ${WANT_LIMIT} active listings at once, in any mix of wants and spares. Remove a listing to free a slot; nothing expires on its own.`,
  },
  {
    q: "Is my account information shown on the public want list?",
    a: "Only your display name is shown beside a listing. Your email, account ID and collection stay private, and you can delete any listing at any time.",
  },
];

export const Route = createFileRoute("/want-list")({
  loader: () => listWantBoard(),
  errorComponent: DefaultRouteError,
  notFoundComponent: DefaultNotFound,
  head: () => ({
    meta: [
      { title: "Palworld TCG Want List — Trade Board | Pal Stars" },
      {
        name: "description",
        content:
          "Public Palworld TCG want list: see which Palworld card game cards collectors are hunting, who holds spares, and which cards are matched and ready to trade.",
      },
      { property: "og:title", content: "Palworld TCG Want List & Trade Board" },
      {
        property: "og:description",
        content:
          "Browse the public Palworld card game want list — wants, spares and matched cards ready to swap. No account needed to look.",
      },
      { property: "og:url", content: "https://palstars.com/want-list" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/want-list" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Pal Stars", item: "https://palstars.com/" },
                { "@type": "ListItem", position: 2, name: "Want List", item: "https://palstars.com/want-list" },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "WebPage",
              name: "Palworld TCG want list and trade board",
              speakable: { "@type": "SpeakableSpecification", cssSelector: [".aeo-answer"] },
            },
          ],
        }),
      },
    ],
  }),
  component: WantListPage,
});

function cardMeta(code: string) {
  const card = OFFICIAL_CARD_BY_CODE.get(code.toUpperCase());
  return {
    name: card?.name ?? code,
    rarity: card?.rarity ?? "",
    image: card?.image ?? officialCardImage(code),
  };
}

function EntryChip({ entry }: { entry: WantEntry }) {
  return (
    <li className="rounded-lg border border-border/50 px-2.5 py-1.5">
      <span className="font-medium">{entry.owner_name}</span>
      {entry.note && <span className="text-muted-foreground"> — {entry.note}</span>}
    </li>
  );
}

function BoardCard({ card }: { card: WantBoardCard }) {
  const meta = cardMeta(card.code);
  const matched = isMatched(card);
  return (
    <article className="glass rounded-2xl p-4">
      <div className="flex gap-4">
        <Link {...cardLinkProps(card.code)} className="shrink-0">
          {meta.image ? (
            <CardImage src={meta.image} alt={`${meta.name} (${card.code}) Palworld card`} width={92} height={128} />
          ) : (
            <span className="flex h-32 w-[92px] items-center justify-center rounded-md border border-border/50 text-xs text-muted-foreground">
              {card.code}
            </span>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link {...cardLinkProps(card.code)} className="font-bold hover:text-primary">
              {meta.name}
            </Link>
            <span className="font-mono text-xs text-muted-foreground">{card.code}</span>
            {meta.rarity && (
              <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px]">{meta.rarity}</span>
            )}
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                matched ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              }`}
            >
              {matched ? "Matched — someone has a spare" : "No spare listed yet"}
            </span>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <h3 className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Wanted by {card.wants.length}
              </h3>
              <ul className="mt-1.5 space-y-1 text-xs">
                {card.wants.map((e) => (
                  <EntryChip key={e.id} entry={e} />
                ))}
                {card.wants.length === 0 && <li className="text-muted-foreground">Nobody yet.</li>}
              </ul>
            </div>
            <div>
              <h3 className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Spares from {card.haves.length}
              </h3>
              <ul className="mt-1.5 space-y-1 text-xs">
                {card.haves.map((e) => (
                  <EntryChip key={e.id} entry={e} />
                ))}
                {card.haves.length === 0 && <li className="text-muted-foreground">No spares listed.</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function WantListPage() {
  const initial = Route.useLoaderData() as WantEntry[];
  const { session } = useSession();
  const signedIn = !!session;

  const boardFn = useServerFn(listWantBoard);
  const mineFn = useServerFn(getMyWantList);
  const addFn = useServerFn(addWantEntry);
  const removeFn = useServerFn(removeWantEntry);

  const [rows, setRows] = useState<WantEntry[]>(initial);
  const [mine, setMine] = useState<WantEntry[]>([]);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState<"all" | "matched" | "want" | "have">("all");
  const [kind, setKind] = useState<WantKind>("want");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const board = (await boardFn()) as WantEntry[];
    setRows(board);
    if (signedIn) {
      try {
        setMine((await mineFn()) as WantEntry[]);
      } catch {
        setMine([]);
      }
    } else {
      setMine([]);
    }
  }, [boardFn, mineFn, signedIn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const board = useMemo(() => groupWantBoard(rows), [rows]);

  const shown = useMemo(() => {
    const q = filter.trim().toUpperCase();
    return board.filter((c) => {
      if (view === "matched" && !isMatched(c)) return false;
      if (view === "want" && c.wants.length === 0) return false;
      if (view === "have" && c.haves.length === 0) return false;
      if (!q) return true;
      return c.code.includes(q) || cardMeta(c.code).name.toUpperCase().includes(q);
    });
  }, [board, filter, view]);

  const suggestions = useMemo(() => {
    const q = code.trim();
    if (q.length < 2) return [];
    return searchOfficialCards(q, 6);
  }, [code]);

  const matchedCount = board.filter(isMatched).length;

  const submit = async () => {
    if (!code.trim()) return;
    const resolved = resolveOfficialCardCode(code);
    if (!resolved) {
      setNotice(`"${code.trim()}" is not an official Palworld TCG printing — pick one of the suggested cards.`);
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await addFn({ data: { kind, cardCode: resolved, note: note.trim() } });
      setCode("");
      setNote("");
      await refresh();
      setNotice(kind === "want" ? "Added to your want list." : "Spare listed on the board.");
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That listing could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const drop = async (id: string) => {
    setBusy(true);
    try {
      await removeFn({ data: { id } });
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That listing could not be removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Trade board</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld TCG Want List</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          This is the public Palworld TCG want list: every card Pal Stars collectors are hunting, next to the spare
          cards other members can trade away. Anyone can read the board without an account — {board.length} cards are
          listed right now and {matchedCount} are already matched, meaning one member wants the card and another has a
          spare of the same printing ready to swap.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Found a match? Send the swap from the{" "}
          <Link to="/trades" className="text-primary hover:underline">
            Palworld card trading page
          </Link>
          , check what the card is worth on the{" "}
          <Link to="/tcg-price-index" className="text-primary hover:underline">
            TCG price index
          </Link>
          , or open free packs on the{" "}
          <Link to="/booster-pack" className="text-primary hover:underline">
            booster pack page
          </Link>
          .
        </p>
      </header>

      <section className="mt-8 glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Post a card</h2>
        {signedIn ? (
          <>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground">Listing type</span>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as WantKind)}
                  className="mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="want">I want this card</option>
                  <option value="have">I have a spare</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground">Card name or code</span>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  list="want-card-codes"
                  placeholder="EBP01-001 or Cattiva"
                  className="mt-1 w-56 rounded-md border bg-background px-3 py-2 text-sm"
                />
              </label>
              <datalist id="want-card-codes">
                {(suggestions.length ? suggestions : PLAYABLE_CARDS.slice(0, 40)).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} — {c.code}
                  </option>
                ))}
              </datalist>
              <label className="text-sm flex-1 min-w-[200px]">
                <span className="block text-xs text-muted-foreground">Note (optional)</span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={200}
                  placeholder="Near mint only, happy to trade two commons"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </label>
              <button
                onClick={() => void submit()}
                disabled={busy || !code.trim()}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
              >
                Add to board
              </button>
            </div>
            {notice && <p className="mt-3 text-sm text-primary">{notice}</p>}

            <h3 className="mt-6 text-sm font-bold">
              Your listings ({mine.length}/{WANT_LIMIT})
            </h3>
            <ul className="mt-2 flex flex-wrap gap-2 text-xs">
              {mine.map((m) => (
                <li key={m.id} className="flex items-center gap-2 rounded-full border border-border/60 px-3 py-1">
                  <span className={m.kind === "have" ? "text-primary" : ""}>
                    {m.kind === "have" ? "Spare" : "Want"}
                  </span>
                  <span className="font-mono">{m.card_code}</span>
                  <button
                    onClick={() => void drop(m.id)}
                    disabled={busy}
                    aria-label={`Remove ${m.card_code} from your want list`}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    ×
                  </button>
                </li>
              ))}
              {mine.length === 0 && <li className="text-muted-foreground">Nothing listed yet.</li>}
            </ul>
          </>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            Browsing is open to everyone.{" "}
            <Link to="/auth" className="text-primary hover:underline">
              Sign in
            </Link>{" "}
            to post the cards you want and the spares you can trade — it is free, and you keep up to {WANT_LIMIT} active
            listings.
          </p>
        )}
      </section>

      <section className="mt-8">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-bold">The board</h2>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter the want list by card name or code"
            placeholder="Filter by card…"
            className="rounded-md border bg-background px-3 py-2 text-sm"
          />
          <div className="flex gap-1 text-xs">
            {(
              [
                ["all", "All cards"],
                ["matched", "Matched"],
                ["want", "Wanted"],
                ["have", "Spares"],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={`rounded-full border px-3 py-1 ${
                  view === v ? "border-primary text-primary" : "border-border/60 text-muted-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {shown.map((c) => (
            <BoardCard key={c.code} card={c} />
          ))}
        </div>
        {shown.length === 0 && (
          <p className="mt-4 text-sm text-muted-foreground">
            No listings match that filter yet. Be the first to post the card you are hunting.
          </p>
        )}
      </section>

      <ClusterLinks hub="/want-list" heading="Palworld TCG want list guides" />
      <FaqSection items={FAQ} heading="Palworld want list FAQ" />
    </div>
  );
}
